import { useState } from 'react';
import styles from './BuildAScreen.module.css';

const TEMPLATES = [
  { id: 'landing', label: 'Landing Page' },
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'mobile', label: 'Mobile App' },
];

const FONT_PAIRINGS = [
  { id: 'modern',    label: 'Modern',    heading: 'Inter, sans-serif',                 body: 'Inter, sans-serif' },
  { id: 'elegant',   label: 'Elegant',   heading: '"Playfair Display", Georgia, serif', body: 'Inter, sans-serif' },
  { id: 'bold',      label: 'Bold',      heading: 'Oswald, sans-serif',                body: 'Inter, sans-serif' },
  { id: 'friendly',  label: 'Friendly',  heading: 'Poppins, sans-serif',               body: 'Poppins, sans-serif' },
  { id: 'technical', label: 'Technical', heading: '"Fira Code", monospace',             body: 'Inter, sans-serif' },
];

const BTN_RADIUS = { rounded: '8px', square: '2px', pill: '999px' };

function getCardStyle(style, primary, dark) {
  const base = dark ? '#1A1F3A' : '#fff';
  if (style === 'flat')     return { background: dark ? '#0F1428' : '#F8FAFC', border: 'none', boxShadow: 'none', borderRadius: '8px' };
  if (style === 'shadow')   return { background: base, border: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.10)', borderRadius: '8px' };
  if (style === 'bordered') return { background: base, border: `1.5px solid ${primary}55`, boxShadow: 'none', borderRadius: '8px' };
  if (style === 'glass')    return { background: dark ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.72)', border: `1px solid ${primary}44`, backdropFilter: 'blur(8px)', boxShadow: `0 4px 24px ${primary}22`, borderRadius: '8px' };
  return { background: base, border: `1px solid ${dark ? 'rgba(255,255,255,0.07)' : '#E2E8F0'}`, borderRadius: '8px' };
}

function getSectionBg(type, primary, accent, dark) {
  const base = dark ? '#0A0E27' : '#F1F5F9';
  if (type === 'gradient') return { background: `linear-gradient(135deg, ${primary}28, ${accent}28)` };
  if (type === 'dots')     return { background: base, backgroundImage: `radial-gradient(${primary}55 1.5px, transparent 1.5px)`, backgroundSize: '16px 16px' };
  return { background: base };
}

const Pills = ({ options, value, onChange }) => (
  <div className={styles.pills}>
    {options.map(o => (
      <button
        key={o.id}
        className={`${styles.pill} ${value === o.id ? styles.pillActive : ''}`}
        onClick={() => onChange(o.id)}
      >
        {o.label}
      </button>
    ))}
  </div>
);

const LandingPreview = ({ primary, accent, headline, tagline, dark, font, buttonShape, brandName, heroAlign, card, section }) => {
  const fp = FONT_PAIRINGS.find(f => f.id === font) || FONT_PAIRINGS[0];
  const bg = dark ? '#0A0E27' : '#fff';
  const text = dark ? '#F8FAFC' : '#1E293B';
  const subText = dark ? '#94A3B8' : '#64748B';
  const navBorder = dark ? 'rgba(255,255,255,0.07)' : '#E2E8F0';
  const cs = getCardStyle(card, primary, dark);
  const sb = getSectionBg(section, primary, accent, dark);
  const aligned = heroAlign === 'center';

  return (
    <div className={styles.frameLanding} style={{ background: bg, color: text, fontFamily: fp.body }}>
      <div className={styles.previewNav} style={{ borderBottom: `1px solid ${navBorder}` }}>
        {brandName ? (
          <span style={{ fontFamily: fp.heading, fontWeight: 700, fontSize: '0.8rem', color: primary }}>{brandName}</span>
        ) : (
          <div className={styles.previewNavLogo} style={{ background: primary }} />
        )}
        <div className={styles.previewNavLinks}>
          {[1, 2, 3].map(i => (
            <div key={i} className={styles.previewNavLink} style={{ background: subText + '44' }} />
          ))}
          <div className={styles.previewNavCta} style={{ background: primary, borderRadius: BTN_RADIUS[buttonShape] }} />
        </div>
      </div>

      <div
        className={styles.previewHero}
        style={{ background: primary + '18', alignItems: aligned ? 'center' : 'flex-start', textAlign: aligned ? 'center' : 'left' }}
      >
        <div className={styles.previewBadge} style={{ background: accent + '33', color: accent }}>New release</div>
        <div className={styles.previewH1} style={{ color: text, fontFamily: fp.heading }}>
          {headline || 'Your headline here'}
        </div>
        <div className={styles.previewTagline} style={{ color: subText }}>
          {tagline || 'Your tagline goes here'}
        </div>
        <div className={styles.previewCtaRow} style={{ justifyContent: aligned ? 'center' : 'flex-start' }}>
          <div className={styles.previewCtaBtn} style={{ background: primary, color: '#fff', borderRadius: BTN_RADIUS[buttonShape] }}>
            Get started
          </div>
          <div className={styles.previewCtaOutline} style={{ border: `1.5px solid ${primary}`, color: primary, borderRadius: BTN_RADIUS[buttonShape] }}>
            Learn more
          </div>
        </div>
      </div>

      <div className={styles.previewCards} style={sb}>
        {[accent, primary, accent + 'cc'].map((c, i) => (
          <div key={i} className={styles.previewCard} style={cs}>
            <div className={styles.previewCardIcon} style={{ background: c }} />
            <div className={styles.previewCardTitle} style={{ background: text + 'cc' }} />
            <div className={styles.previewCardLine} style={{ background: text + '44' }} />
            <div className={styles.previewCardLine} style={{ background: text + '33', width: '70%' }} />
          </div>
        ))}
      </div>
    </div>
  );
};

const DashboardPreview = ({ primary, accent, dark, font, card, section }) => {
  const fp = FONT_PAIRINGS.find(f => f.id === font) || FONT_PAIRINGS[0];
  const bg = dark ? '#0A0E27' : '#F1F5F9';
  const sidebarBg = dark ? '#1A1F3A' : '#fff';
  const border = dark ? 'rgba(255,255,255,0.07)' : '#E2E8F0';
  const text = dark ? '#F8FAFC' : '#1E293B';
  const sub = dark ? '#94A3B8' : '#64748B';
  const cs = getCardStyle(card, primary, dark);
  const sb = getSectionBg(section, primary, accent, dark);

  return (
    <div className={styles.frameDashboard} style={{ background: bg }}>
      <div className={styles.dashSidebar} style={{ background: sidebarBg, borderRight: `1px solid ${border}` }}>
        <div className={styles.dashLogo} style={{ background: primary }} />
        {[1, 2, 3, 4].map(i => (
          <div key={i} className={`${styles.dashMenuItem} ${i === 1 ? styles.dashMenuItemActive : ''}`}
            style={{ background: i === 1 ? primary + '22' : 'transparent' }}>
            <div className={styles.dashMenuIcon} style={{ background: i === 1 ? primary : sub + '66' }} />
            <div className={styles.dashMenuLabel} style={{ background: text + (i === 1 ? 'cc' : '55') }} />
          </div>
        ))}
      </div>
      <div className={styles.dashMain}>
        <div className={styles.dashHeader} style={{ background: sidebarBg, borderBottom: `1px solid ${border}` }}>
          <div className={styles.dashHeaderTitle} style={{ background: text + 'cc', fontFamily: fp.heading }} />
          <div className={styles.dashHeaderAvatar} style={{ background: primary }} />
        </div>
        <div className={styles.dashContent} style={{ padding: '0.75rem', ...sb }}>
          <div className={styles.dashStats}>
            {[primary, accent, primary + 'aa', accent + 'aa'].map((c, i) => (
              <div key={i} className={styles.dashStat} style={cs}>
                <div className={styles.dashStatVal} style={{ background: text + 'cc' }} />
                <div className={styles.dashStatLabel} style={{ background: sub + '77' }} />
                <div className={styles.dashStatBar} style={{ background: c }} />
              </div>
            ))}
          </div>
          <div className={styles.dashRow}>
            <div className={styles.dashChart} style={cs}>
              {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
                <div key={i} className={styles.dashBar} style={{ height: `${h}%`, background: i % 2 === 0 ? primary : accent }} />
              ))}
            </div>
            <div className={styles.dashList} style={cs}>
              {[1, 2, 3].map(i => (
                <div key={i} className={styles.dashListRow} style={{ borderBottom: i < 3 ? `1px solid ${border}` : 'none' }}>
                  <div className={styles.dashListDot} style={{ background: i === 1 ? primary : sub }} />
                  <div className={styles.dashListText} style={{ background: text + '88' }} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const MobilePreview = ({ primary, accent, headline, dark, font, buttonShape, heroAlign, card, section }) => {
  const fp = FONT_PAIRINGS.find(f => f.id === font) || FONT_PAIRINGS[0];
  const bg = dark ? '#0A0E27' : '#F8FAFC';
  const navBg = dark ? '#1A1F3A' : '#fff';
  const border = dark ? 'rgba(255,255,255,0.07)' : '#E2E8F0';
  const text = dark ? '#F8FAFC' : '#1E293B';
  const sub = dark ? '#94A3B8' : '#64748B';
  const cs = getCardStyle(card, primary, dark);
  const sb = getSectionBg(section, primary, accent, dark);
  const aligned = heroAlign === 'center';

  return (
    <div className={styles.phoneFrame}>
      <div className={styles.phoneInner} style={{ background: bg }}>
        <div className={styles.phoneBar} style={{ background: primary }}>
          <div className={styles.phoneBarTitle} style={{ background: 'rgba(255,255,255,0.8)' }} />
        </div>
        <div style={{ padding: '0.5rem' }}>
          <div
            className={styles.phoneHero}
            style={{ background: primary + '22', borderRadius: 6, alignItems: aligned ? 'center' : 'flex-start' }}
          >
            <div className={styles.phoneBadge} style={{ background: accent + '44', color: accent }}>New</div>
            <div className={styles.phoneH1} style={{ background: text + 'cc' }} />
            <div className={styles.phoneSub} style={{ background: sub + '66' }} />
          </div>
          <div className={styles.phoneSection} style={sb}>
            {[1, 2, 3].map(i => (
              <div key={i} className={styles.phoneCard} style={cs}>
                <div className={styles.phoneCardIcon} style={{ background: i % 2 === 0 ? primary : accent }} />
                <div className={styles.phoneCardContent}>
                  <div className={styles.phoneCardTitle} style={{ background: text + 'cc' }} />
                  <div className={styles.phoneCardSub} style={{ background: sub + '66' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className={styles.phoneNav} style={{ background: navBg, borderTop: `1px solid ${border}` }}>
          {[1, 2, 3, 4].map(i => (
            <div key={i} className={styles.phoneNavItem}>
              <div className={styles.phoneNavIcon} style={{ background: i === 1 ? primary : sub + '66' }} />
              <div className={styles.phoneNavLabel} style={{ background: i === 1 ? primary : sub + '55' }} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const BuildAScreen = () => {
  const [template, setTemplate]       = useState('landing');
  const [primary, setPrimary]         = useState('#00BCD4');
  const [accent, setAccent]           = useState('#FF6B35');
  const [headline, setHeadline]       = useState('');
  const [tagline, setTagline]         = useState('');
  const [dark, setDark]               = useState(false);
  const [font, setFont]               = useState('modern');
  const [buttonShape, setButtonShape] = useState('rounded');
  const [brandName, setBrandName]     = useState('');
  const [heroAlign, setHeroAlign]     = useState('left');
  const [card, setCard]               = useState('shadow');
  const [section, setSection]         = useState('solid');

  const PreviewComponent =
    template === 'landing'   ? LandingPreview :
    template === 'dashboard' ? DashboardPreview :
    MobilePreview;

  const previewProps = { primary, accent, headline, tagline, dark, font, buttonShape, brandName, heroAlign, card, section };

  return (
    <div className={styles.wrap}>
      <div className={styles.controls}>
        <div className={styles.controlSection}>
          <p className={styles.controlLabel}>Template</p>
          <div className={styles.templateGroup}>
            {TEMPLATES.map(t => (
              <button
                key={t.id}
                className={`${styles.templateBtn} ${template === t.id ? styles.templateBtnActive : ''}`}
                onClick={() => setTemplate(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className={styles.controlSection}>
          <p className={styles.controlLabel}>Colours</p>
          <div className={styles.colorRow}>
            <label className={styles.colorItem}>
              <input type="color" className={styles.colorPicker} value={primary} onChange={e => setPrimary(e.target.value)} />
              <span>Primary</span>
            </label>
            <label className={styles.colorItem}>
              <input type="color" className={styles.colorPicker} value={accent} onChange={e => setAccent(e.target.value)} />
              <span>Accent</span>
            </label>
          </div>
        </div>

        <div className={styles.controlSection}>
          <p className={styles.controlLabel}>Brand name</p>
          <input
            className={styles.textInput}
            type="text"
            placeholder="e.g. Kilonova"
            value={brandName}
            onChange={e => setBrandName(e.target.value)}
            maxLength={20}
          />
        </div>

        {template !== 'dashboard' && (
          <div className={styles.controlSection}>
            <p className={styles.controlLabel}>Text</p>
            <input
              className={styles.textInput}
              type="text"
              placeholder="Headline"
              value={headline}
              onChange={e => setHeadline(e.target.value)}
              maxLength={40}
            />
            <input
              className={styles.textInput}
              type="text"
              placeholder="Tagline"
              value={tagline}
              onChange={e => setTagline(e.target.value)}
              maxLength={60}
            />
          </div>
        )}

        <div className={styles.controlSection}>
          <p className={styles.controlLabel}>Font pairing</p>
          <Pills options={FONT_PAIRINGS} value={font} onChange={setFont} />
        </div>

        {template !== 'dashboard' && (
          <div className={styles.controlSection}>
            <p className={styles.controlLabel}>Hero alignment</p>
            <Pills
              options={[{ id: 'left', label: 'Left' }, { id: 'center', label: 'Centre' }]}
              value={heroAlign}
              onChange={setHeroAlign}
            />
          </div>
        )}

        <div className={styles.controlSection}>
          <p className={styles.controlLabel}>Button shape</p>
          <Pills
            options={[{ id: 'rounded', label: 'Rounded' }, { id: 'square', label: 'Square' }, { id: 'pill', label: 'Pill' }]}
            value={buttonShape}
            onChange={setButtonShape}
          />
        </div>

        <div className={styles.controlSection}>
          <p className={styles.controlLabel}>Card style</p>
          <Pills
            options={[{ id: 'flat', label: 'Flat' }, { id: 'shadow', label: 'Shadow' }, { id: 'bordered', label: 'Bordered' }, { id: 'glass', label: 'Glass' }]}
            value={card}
            onChange={setCard}
          />
        </div>

        <div className={styles.controlSection}>
          <p className={styles.controlLabel}>Section background</p>
          <Pills
            options={[{ id: 'solid', label: 'Solid' }, { id: 'gradient', label: 'Gradient' }, { id: 'dots', label: 'Dots' }]}
            value={section}
            onChange={setSection}
          />
        </div>

        <div className={styles.controlSection}>
          <label className={styles.toggleRow}>
            <span className={styles.controlLabel} style={{ margin: 0 }}>Dark mode</span>
            <button
              className={`${styles.toggle} ${dark ? styles.toggleOn : ''}`}
              onClick={() => setDark(d => !d)}
              aria-label="Toggle dark mode"
            >
              <span className={styles.toggleThumb} />
            </button>
          </label>
        </div>
      </div>

      <div className={styles.previewWrap}>
        <div className={styles.previewChrome}>
          <div className={styles.chromeDots}>
            <span className={styles.dot} style={{ background: '#FF5F57' }} />
            <span className={styles.dot} style={{ background: '#FEBC2E' }} />
            <span className={styles.dot} style={{ background: '#28C840' }} />
          </div>
          <div className={styles.chromeBar} />
        </div>
        <div className={styles.previewViewport}>
          <PreviewComponent {...previewProps} />
        </div>
      </div>
    </div>
  );
};

export default BuildAScreen;
